/**
 * country-timezone
 * For Media Devoted | Created By Ciro Autiero
 *
 * Resolves a country (key / name / ISO2) to a single representative IANA
 * timezone so the UI can show the recipient's local wall-clock time.
 * Multi-zone countries use their primary business zone (US → America/New_York,
 * AU → Australia/Sydney) — the same zones Automations suggests for
 * recipient-local send windows. Unknown countries return `null` (UI shows —).
 */

import { APP_DISPLAY_TIMEZONE, formatInZone } from '@/lib/datetime-timezone';
import { countryKeyToIso2, countryToFlagEmoji } from '@/utils/country-flag-emoji';

/** ISO2 → primary IANA timezone. One representative zone per country. */
const TZ_BY_ISO2_DATA = `
AD|Europe/Andorra
AE|Asia/Dubai
AF|Asia/Kabul
AL|Europe/Tirane
AM|Asia/Yerevan
AO|Africa/Luanda
AR|America/Argentina/Buenos_Aires
AT|Europe/Vienna
AU|Australia/Sydney
AX|Europe/Mariehamn
AZ|Asia/Baku
BA|Europe/Sarajevo
BB|America/Barbados
BD|Asia/Dhaka
BE|Europe/Brussels
BF|Africa/Ouagadougou
BG|Europe/Sofia
BH|Asia/Bahrain
BJ|Africa/Porto-Novo
BM|Atlantic/Bermuda
BN|Asia/Brunei
BO|America/La_Paz
BR|America/Sao_Paulo
BS|America/Nassau
BT|Asia/Thimphu
BW|Africa/Gaborone
BY|Europe/Minsk
BZ|America/Belize
CA|America/Toronto
CD|Africa/Kinshasa
CG|Africa/Brazzaville
CH|Europe/Zurich
CI|Africa/Abidjan
CL|America/Santiago
CM|Africa/Douala
CN|Asia/Shanghai
CO|America/Bogota
CR|America/Costa_Rica
CU|America/Havana
CV|Atlantic/Cape_Verde
CY|Asia/Nicosia
CZ|Europe/Prague
DE|Europe/Berlin
DK|Europe/Copenhagen
DO|America/Santo_Domingo
DZ|Africa/Algiers
EC|America/Guayaquil
EE|Europe/Tallinn
EG|Africa/Cairo
ES|Europe/Madrid
ET|Africa/Addis_Ababa
FI|Europe/Helsinki
FJ|Pacific/Fiji
FO|Atlantic/Faroe
FR|Europe/Paris
GA|Africa/Libreville
GB|Europe/London
GE|Asia/Tbilisi
GG|Europe/Guernsey
GH|Africa/Accra
GI|Europe/Gibraltar
GL|America/Nuuk
GN|Africa/Conakry
GR|Europe/Athens
GT|America/Guatemala
GU|Pacific/Guam
GY|America/Guyana
HK|Asia/Hong_Kong
HN|America/Tegucigalpa
HR|Europe/Zagreb
HT|America/Port-au-Prince
HU|Europe/Budapest
ID|Asia/Jakarta
IE|Europe/Dublin
IL|Asia/Jerusalem
IM|Europe/Isle_of_Man
IN|Asia/Kolkata
IQ|Asia/Baghdad
IR|Asia/Tehran
IS|Atlantic/Reykjavik
IT|Europe/Rome
JE|Europe/Jersey
JM|America/Jamaica
JO|Asia/Amman
JP|Asia/Tokyo
KE|Africa/Nairobi
KG|Asia/Bishkek
KH|Asia/Phnom_Penh
KP|Asia/Pyongyang
KR|Asia/Seoul
KW|Asia/Kuwait
KZ|Asia/Almaty
LA|Asia/Vientiane
LB|Asia/Beirut
LI|Europe/Vaduz
LK|Asia/Colombo
LT|Europe/Vilnius
LU|Europe/Luxembourg
LV|Europe/Riga
LY|Africa/Tripoli
MA|Africa/Casablanca
MC|Europe/Monaco
MD|Europe/Chisinau
ME|Europe/Podgorica
MG|Indian/Antananarivo
MK|Europe/Skopje
ML|Africa/Bamako
MM|Asia/Yangon
MN|Asia/Ulaanbaatar
MO|Asia/Macau
MT|Europe/Malta
MU|Indian/Mauritius
MV|Indian/Maldives
MX|America/Mexico_City
MY|Asia/Kuala_Lumpur
MZ|Africa/Maputo
NA|Africa/Windhoek
NC|Pacific/Noumea
NE|Africa/Niamey
NG|Africa/Lagos
NI|America/Managua
NL|Europe/Amsterdam
NO|Europe/Oslo
NP|Asia/Kathmandu
NZ|Pacific/Auckland
OM|Asia/Muscat
PA|America/Panama
PE|America/Lima
PF|Pacific/Tahiti
PG|Pacific/Port_Moresby
PH|Asia/Manila
PK|Asia/Karachi
PL|Europe/Warsaw
PR|America/Puerto_Rico
PS|Asia/Gaza
PT|Europe/Lisbon
PY|America/Asuncion
QA|Asia/Qatar
RE|Indian/Reunion
RO|Europe/Bucharest
RS|Europe/Belgrade
RU|Europe/Moscow
RW|Africa/Kigali
SA|Asia/Riyadh
SC|Indian/Mahe
SD|Africa/Khartoum
SE|Europe/Stockholm
SG|Asia/Singapore
SI|Europe/Ljubljana
SK|Europe/Bratislava
SM|Europe/San_Marino
SN|Africa/Dakar
SO|Africa/Mogadishu
SR|America/Paramaribo
SS|Africa/Juba
SV|America/El_Salvador
SY|Asia/Damascus
SZ|Africa/Mbabane
TD|Africa/Ndjamena
TG|Africa/Lome
TH|Asia/Bangkok
TJ|Asia/Dushanbe
TM|Asia/Ashgabat
TN|Africa/Tunis
TO|Pacific/Tongatapu
TR|Europe/Istanbul
TT|America/Port_of_Spain
TW|Asia/Taipei
TZ|Africa/Dar_es_Salaam
UA|Europe/Kyiv
UG|Africa/Kampala
US|America/New_York
UY|America/Montevideo
UZ|Asia/Tashkent
VA|Europe/Vatican
VE|America/Caracas
VN|Asia/Ho_Chi_Minh
VU|Pacific/Efate
WS|Pacific/Apia
YE|Asia/Aden
YT|Indian/Mayotte
ZA|Africa/Johannesburg
ZM|Africa/Lusaka
ZW|Africa/Harare
`.trim();

const TZ_BY_ISO2: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const line of TZ_BY_ISO2_DATA.split('\n')) {
    const [iso2, tz] = line.split('|');
    if (iso2 && tz) map[iso2.trim().toUpperCase()] = tz.trim();
  }
  return map;
})();

/** Representative IANA timezone for a country key/name/ISO2. `null` when unknown. */
export function countryKeyToTimezone(countryKey: string | null | undefined): string | null {
  const raw = (countryKey ?? '').trim();
  if (!raw) return null;
  // countryKeyToIso2 falls back to the first two letters for unrecognized names
  // (e.g. "atlantis" → "AT"), which would silently show a wrong zone. Gate on the
  // flag resolver's 🌍 unknown-sentinel so only genuine countries resolve.
  const isIso2 = /^[A-Za-z]{2}$/.test(raw);
  if (!isIso2 && countryToFlagEmoji(raw) === '🌍') return null;
  return TZ_BY_ISO2[countryKeyToIso2(raw).toUpperCase()] ?? null;
}

export interface CountryLocalSend {
  /** Local wall-clock time, 'HH:mm' (24h). */
  time: string;
  /** Full local timestamp, 'DD/MM/YYYY HH:mm'. */
  full: string;
  /** Local calendar day vs Danish (display) day: -1 | 0 | 1. */
  dayDelta: number;
  /** Human tz label, e.g. 'Sydney UTC+10'. */
  tzLabel: string;
}

/** 'DD/MM/YYYY' → sortable YYYYMMDD number (for day comparison). */
function dayNumber(dmy: string): number {
  const [d, m, y] = dmy.split('/');
  return Number(`${y}${m}${d}`);
}

/** 'Australia/Sydney' + instant → 'Sydney UTC+10' (live offset). */
function offsetLabel(tz: string, at: Date): string {
  const city = tz.split('/').pop()?.replace(/_/g, ' ') ?? tz;
  const off =
    new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName')?.value ?? '';
  return `${city} ${off.replace('GMT', 'UTC')}`.trim();
}

/**
 * Describe a UTC ISO instant in the recipient country's local time.
 * `null` when the country has no known timezone or the instant is invalid.
 */
export function describeCountryLocalSend(
  iso: string | null | undefined,
  countryKey: string | null | undefined,
): CountryLocalSend | null {
  const tz = countryKeyToTimezone(countryKey);
  if (!tz || !iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const full = formatInZone(at, tz);
  const dkDay = dayNumber(formatInZone(at, APP_DISPLAY_TIMEZONE).slice(0, 10));
  const localDay = dayNumber(full.slice(0, 10));
  const dayDelta = localDay === dkDay ? 0 : localDay > dkDay ? 1 : -1;
  return { time: full.slice(11), full, dayDelta, tzLabel: offsetLabel(tz, at) };
}

/**
 * Sortable key for the recipient's local send instant, 'YYYYMMDDHHmm' (local
 * calendar date + wall-clock, so lexical order == chronological local order).
 * Returns '' when the timezone or instant can't be resolved, so those rows sort
 * together. In a single-country list every row shares a zone, so this matches the
 * planned-send (executeAfter) order; it only diverges across countries (offsets).
 */
export function countryLocalSendSortValue(
  iso: string | null | undefined,
  countryKey: string | null | undefined,
): string {
  const local = describeCountryLocalSend(iso, countryKey);
  if (!local) return '';
  const [dmy, hm = ''] = local.full.split(' ');
  const [d, m, y] = dmy.split('/');
  return `${y}${m}${d}${hm.replace(':', '')}`;
}
